import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Card } from '../ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { DialogoConfirmar } from '../ui/confirmar';
import { fmtFull } from '../../lib/format';
import { KeyRound, ExternalLink, Ban, Unlock, LogOut } from 'lucide-react';

/** Admin → «Accesos SSO»: quién entra a qué proyecto con Orvian SSO (sso.orvian.mx).
 *  La identidad es el buzón de /email; los proyectos y permisos los guarda el SSO.
 *  Proyectos con regla «cuenta_en_app» (p. ej. Aviron) no llevan interruptor: deja
 *  entrar el propio proyecto si encuentra una cuenta con ese correo. */
export default function PanelSso({ yo }) {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState('');
  const [ocupado, setOcupado] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const { data, error, isLoading } = useQuery({
    queryKey: ['admin-sso'],
    queryFn: async () => (await api.get('/admin/sso')).data,
    retry: false,
  });

  const permitidos = useMemo(() => {
    const s = new Set();
    (data?.accesos || []).forEach((a) => s.add(`${a.email}|${a.app}`));
    return s;
  }, [data]);

  const personas = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    return (data?.personas || []).filter((p) => !f || p.email.includes(f) || (p.nombre || '').toLowerCase().includes(f));
  }, [data, filtro]);

  const apps = (data?.aplicaciones || []).filter((a) => a.activa);

  async function accion(clave, fn, ok) {
    setOcupado(clave);
    try {
      await fn();
      if (ok) toast.success(ok);
      await qc.invalidateQueries({ queryKey: ['admin-sso'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo completar');
    } finally {
      setOcupado(null);
    }
  }

  const alternar = (p, app, valor) => accion(`${p.email}|${app.clave}`,
    () => api.put('/admin/sso/accesos', { email: p.email, app: app.clave, permitido: valor }));

  const ejecutarBloqueo = (p, valor) => accion(`bloqueo|${p.email}`,
    () => api.put('/admin/sso/bloqueo', { email: p.email, bloqueada: valor }),
    valor ? 'Acceso SSO suspendido y sesiones cerradas' : 'Acceso SSO reactivado');
  // Suspender quita el acceso y cierra sesiones en un clic: se confirma (A12). Reactivar no.
  const bloquear = (p, valor) => (valor
    ? setConfirmar({
      titulo: `¿Suspender el acceso SSO de ${p.email}?`,
      mensaje: 'Se cierran sus sesiones en todas las aplicaciones de Orvian y no podrá volver a entrar hasta que lo reactives.',
      boton: 'Suspender acceso',
      accion: () => ejecutarBloqueo(p, true),
    })
    : ejecutarBloqueo(p, false));

  const cerrar = (p) => accion(`cerrar|${p.email}`,
    () => api.post('/admin/sso/cerrar-sesiones', { email: p.email }), 'Sesiones SSO cerradas');

  if (isLoading) return <Card className="p-6 text-sm text-muted-foreground">Cargando accesos…</Card>;
  if (error) {
    return (
      <Card className="p-6 text-sm">
        <div className="font-medium mb-1">Orvian SSO no responde</div>
        <div className="text-muted-foreground">{error?.response?.data?.detail || 'Intenta de nuevo en unos minutos.'}</div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-sso">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-medium flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" />Orvian SSO</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-[640px]">
              Un solo acceso para los proyectos de Orvian con el correo y la contraseña del buzón. Aquí decides a qué
              proyecto entra cada persona. Al entrar por SSO el proyecto no vuelve a pedir su verificación en dos pasos;
              con su formulario normal sí. Proyectos marcados «según su cuenta» deciden ellos (el correo debe existir allá).
            </p>
          </div>
          <a className="text-sm text-primary inline-flex items-center gap-1 font-medium" href={data.sso_url} target="_blank" rel="noreferrer">
            Abrir SSO <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar persona…" aria-label="Buscar persona" className="max-w-xs" />
        <span className="text-sm text-muted-foreground">{personas.length} personas · {apps.length} proyectos</span>
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Persona</TableHead>
              {apps.map((a) => (
                <TableHead key={a.clave} className="text-center whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: a.color || 'var(--primary)' }} />
                    {a.nombre}
                  </span>
                </TableHead>
              ))}
              <TableHead>Última entrada</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {personas.length === 0 ? (
              <TableRow><TableCell colSpan={apps.length + 3} className="text-center text-muted-foreground py-6">Sin personas</TableCell></TableRow>
            ) : personas.map((p) => (
              <TableRow key={p.email} data-testid="sso-fila">
                <TableCell>
                  <div className="font-medium flex items-center gap-2">
                    {p.nombre}
                    {p.bloqueada && <Badge variant="destructive">Suspendido</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{p.email}</div>
                </TableCell>
                {apps.map((a) => (
                  <TableCell key={a.clave} className="text-center">
                    {a.regla === 'cuenta_en_app' ? (
                      <span className="text-xs text-muted-foreground" title="Entra si existe una cuenta con este correo en el proyecto">según su cuenta</span>
                    ) : (
                      <Switch
                        checked={permitidos.has(`${p.email}|${a.clave}`)}
                        disabled={p.bloqueada || ocupado === `${p.email}|${a.clave}`}
                        onCheckedChange={(v) => alternar(p, a, v)}
                        aria-label={`${a.nombre} para ${p.email}`}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                  {p.ultima_entrada ? fmtFull(p.ultima_entrada) : 'Nunca'}
                  {p.sesiones > 0 && <div className="text-xs">{p.sesiones} sesión{p.sesiones === 1 ? '' : 'es'} abierta{p.sesiones === 1 ? '' : 's'}</div>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {p.sesiones > 0 && (
                    <Button size="sm" variant="ghost" disabled={ocupado === `cerrar|${p.email}`} onClick={() => cerrar(p)} title="Cerrar sus sesiones del SSO" aria-label={`Cerrar las sesiones SSO de ${p.email}`}>
                      <LogOut className="h-4 w-4" />
                    </Button>
                  )}
                  {p.email !== yo?.email && (
                    p.bloqueada ? (
                      <Button size="sm" variant="ghost" disabled={ocupado === `bloqueo|${p.email}`} onClick={() => bloquear(p, false)} title="Reactivar acceso SSO" aria-label={`Reactivar el acceso SSO de ${p.email}`}>
                        <Unlock className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" disabled={ocupado === `bloqueo|${p.email}`} onClick={() => bloquear(p, true)} title="Suspender acceso SSO" aria-label={`Suspender el acceso SSO de ${p.email}`}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <DialogoConfirmar
        open={!!confirmar}
        titulo={confirmar?.titulo || ''}
        mensaje={confirmar?.mensaje || ''}
        boton={confirmar?.boton}
        testId="sso-confirmar"
        onOpenChange={(v) => !v && setConfirmar(null)}
        onConfirmar={() => { const a = confirmar?.accion; setConfirmar(null); a?.(); }}
      />
    </div>
  );
}
